import * as THREE from 'three';
import { Input } from './core/Input';
import { QUALITY, type QualityLevel, type QualitySettings } from './core/Quality';
import { Pipeline } from './render/Pipeline';
import { Shadows } from './render/Shadows';
import { shared } from './render/SharedUniforms';
import { Atmosphere, MOON_E_REL, SUN_E_TOA } from './sky/Atmosphere';
import { computeCelestial, localToUTC, type CelestialState } from './sky/Celestial';
import { ColliderGrid, Controller } from './player/Controller';
import { TerrainData } from './world/TerrainData';
import { Terrain } from './world/Terrain';
import { buildStrataTexture, STRATA_RANGE, STRATA_Y0 } from './world/Strata';
import { buildDetailNoise } from './world/NoiseTextures';
import { Clouds } from './sky/Clouds';
import { Weather } from './weather/Weather';
import { Precipitation } from './weather/Precipitation';
import { Hoodoos } from './world/Hoodoos';
import { Props, type OsmData } from './world/Props';
import type { InstanceSystem } from './world/InstanceSystem';
import { GuidedHike } from './player/GuidedHike';
import { Vegetation } from './world/Vegetation';
import { loadGroundTextures } from './world/GroundTextures';
import { Lightning } from './weather/Lightning';
import { GpuTimer } from './core/GpuTimer';
import { AudioSystem } from './audio/AudioSystem';

export interface TimeState {
  year: number;
  month: number; // 0-based
  day: number;
  hour: number; // local fractional hour
  rate: number; // simulated hours per real second (0 = paused)
}

export interface Systems {
  update(dt: number, app: App): void;
}

export class App {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly input: Input;
  quality: QualitySettings;
  atmosphere!: Atmosphere;
  shadows!: Shadows;
  pipeline!: Pipeline;
  terrainData!: TerrainData;
  terrain!: Terrain;
  controller!: Controller;
  clouds!: Clouds;
  readonly weather = new Weather();
  precip!: Precipitation;
  hoodoos: Hoodoos | null = null;
  props: Props | null = null;
  vegetation: Vegetation | null = null;
  osm!: OsmData;
  hike: GuidedHike | null = null;
  readonly audio = new AudioSystem();
  readonly lightning = new Lightning();
  gpu!: GpuTimer;
  private trailGrid = new Map<number, [number, number, number, number, boolean][]>();
  readonly instanceSystems: InstanceSystem[] = [];
  readonly loadWarnings: string[] = [];
  readonly colliders = new ColliderGrid(16);
  celestial!: CelestialState;
  readonly time: TimeState = { year: 2026, month: 9, day: 14, hour: 17.4, rate: 0 };
  readonly systems: Systems[] = [];
  private last = performance.now();
  elapsed = 0;
  fps = 60;
  frameMs = 16;
  readonly base = './data/';
  trailTex!: THREE.Texture;
  /** user-facing hooks */
  onFrame: ((dt: number) => void) | null = null;
  ready = false;
  frameCount = 0;

  constructor(readonly canvas: HTMLCanvasElement, level: QualityLevel) {
    this.quality = QUALITY[level];
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: false,
      powerPreference: 'high-performance',
      alpha: false,
      depth: true,
      stencil: false,
      preserveDrawingBuffer: new URLSearchParams(location.search).has('capture'),
    });
    this.renderer.outputColorSpace = THREE.LinearSRGBColorSpace; // final pass writes sRGB itself
    this.renderer.toneMapping = THREE.NoToneMapping;
    this.renderer.autoClear = true;
    this.renderer.shadowMap.enabled = false;
    this.renderer.info.autoReset = false;
    this.camera = new THREE.PerspectiveCamera(70, 1, 0.3, 400000);
    this.gpu = new GpuTimer(this.renderer.getContext() as WebGL2RenderingContext);
    this.input = new Input(canvas);
  }

  async load(onProgress: (f: number, label: string) => void) {
    const maxAniso = this.renderer.capabilities.getMaxAnisotropy();
    shared.uStrata.value = buildStrataTexture();
    shared.uStrataRange.value.set(STRATA_Y0, STRATA_RANGE);
    shared.uNoise.value = buildDetailNoise();

    this.atmosphere = new Atmosphere();
    shared.uSkyIrr.value = this.atmosphere.skyIrr.texture;
    shared.uTransLUT.value = this.atmosphere.transLUT.texture;
    const dummyCloud = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
    dummyCloud.needsUpdate = true;
    shared.uCloudShadow.value = dummyCloud;

    this.terrainData = new TerrainData(this.base);
    await this.terrainData.load((f, l) => onProgress(f * 0.8, l), Math.min(8, maxAniso));
    this.trailTex = await new THREE.TextureLoader().loadAsync(`${this.base}near_trail.png`);
    this.trailTex.flipY = false;
    this.trailTex.colorSpace = THREE.NoColorSpace;
    this.trailTex.minFilter = THREE.LinearMipmapLinearFilter;
    this.trailTex.anisotropy = Math.min(8, maxAniso);
    this.trailTex.needsUpdate = true;
    onProgress(0.85, 'terrain mesh');

    const nearL = this.terrainData.near;
    shared.uTerrainVis.value = nearL.normalTex;
    shared.uTerrainVisB.value.set(nearL.x0, nearL.z0, nearL.size, 1);
    this.shadows = new Shadows(this.quality.shadowSize);
    this.shadows.cascadeCount = this.quality.cascades;
    this.terrain = new Terrain(this.terrainData, this.quality.terrainGrid, this.quality.terrainRange, this.trailTex);
    this.terrain.bindShadowUniforms(this.shadows.uniforms);
    this.shadows.casters.push(this.terrain);
    this.scene.add(this.terrain.mesh);
    try {
      this.terrain.setGround(await loadGroundTextures('./'));
    } catch (e) {
      this.loadWarnings.push(`ground textures: ${e}`);
    }

    this.osm = await (await fetch(`${this.base}osm.json`)).json();
    onProgress(0.86, 'hoodoos');
    try {
      const h = new Hoodoos();
      await h.load('./', this.terrainData, this.colliders, this.shadows.uniforms, this.quality.hoodooLod, Math.min(8, maxAniso), this.quality.hoodooMaxDist);
      this.hoodoos = h;
      this.addInstances(h.system);
      if (h.boulders) this.addInstances(h.boulders);
    } catch (e) {
      this.loadWarnings.push(`hoodoos: ${e}`);
      console.warn('hoodoos unavailable', e);
    }
    onProgress(0.87, 'forest');
    try {
      const v = new Vegetation();
      await v.load('./', this.terrainData, this.terrain, this.colliders, this.shadows.uniforms, this.quality, Math.min(8, maxAniso));
      this.vegetation = v;
      this.shadows.casters.push(v.meshes);
      if (v.impostors) this.shadows.casters.push(v.impostors);
      this.scene.add(v.group);
    } catch (e) {
      this.loadWarnings.push(`vegetation: ${e}`);
      console.warn('vegetation unavailable', e);
    }
    onProgress(0.88, 'trail furniture');
    try {
      const p = new Props();
      await p.load('./', this.terrainData, this.osm, this.colliders, this.shadows.uniforms);
      this.props = p;
      for (const s of p.systems) this.addInstances(s, false);
      this.scene.add(p.group);
    } catch (e) {
      this.loadWarnings.push(`props: ${e}`);
      console.warn('props unavailable', e);
    }
    this.pipeline = new Pipeline(this.renderer, this.atmosphere, {
      renderScale: this.quality.renderScale, msaa: this.quality.msaa, bloomStrength: this.quality.bloom,
    });
    onProgress(0.9, 'cloud noise volumes');
    this.clouds = new Clouds(this.renderer, this.atmosphere, this.quality.cloudScale, this.quality.cloudSteps);
    shared.uCloudShadow.value = this.clouds.shadowRT.texture;
    shared.uCloudShadowXform.value = this.clouds.uniforms.uCloudShadowXform.value;
    this.precip = new Precipitation();
    this.weather.set('clear', true);
    this.controller = new Controller(this.terrainData, this.colliders);
    this.controller.teleport(9, 24, 98, -9);
    this.buildTrailGrid();
    this.controller.surfaceAt = (x, z) => this.surfaceAt(x, z);
    this.controller.onFootstep = (e) => this.audio.footstep(e.surface, e.intensity);
    this.hike = new GuidedHike(this.osm, this.terrainData, this.controller);
    this.hike.onFootstep = (surf, i) => this.audio.footstep(this.weather.snowCover > 0.35 ? 'snow' : surf, i);
    this.weather.onThunder = (delay, intensity) => this.audio.thunder(delay, intensity);
    this.weather.onLightning = (pos) => this.lightning.strike(pos, this.terrainData.heightAt(pos.x, pos.z));
    this.resize();
    window.addEventListener('resize', () => this.resize());
    onProgress(1, 'ready');
    this.ready = true;
  }

  addInstances(sys: InstanceSystem, addGroup = true) {
    this.instanceSystems.push(sys);
    this.shadows.casters.push(sys);
    if (addGroup) this.scene.add(sys.group);
  }

  resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, this.quality.maxDpr);
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.pipeline?.resize(Math.round(w * dpr), Math.round(h * dpr));
    this.clouds?.resize(this.pipeline.compositeRT.width, this.pipeline.compositeRT.height);
  }

  setQuality(level: QualityLevel) {
    this.quality = QUALITY[level];
    this.pipeline.setSettings({ renderScale: this.quality.renderScale, msaa: this.quality.msaa, bloomStrength: this.quality.bloom });
    this.shadows.cascadeCount = this.quality.cascades;
    this.clouds.setQuality(this.quality.cloudScale, this.quality.cloudSteps);
    this.resize();
  }

  get dateUTC(): Date {
    const t = this.time;
    return localToUTC(t.year, t.month, t.day, t.hour);
  }

  private updateCelestial() {
    this.celestial = computeCelestial(this.dateUTC);
    const c = this.celestial;
    const sunUp = c.sunAlt > -4;
    const dir = sunUp || c.moonAlt < 0 ? c.sunDir : c.moonDir;
    shared.uSunDir.value.copy(dir);
    if (sunUp || c.moonAlt < 0) shared.uSunColorTOA.value.setScalar(SUN_E_TOA);
    else {
      const m = SUN_E_TOA * MOON_E_REL * Math.pow(c.moonIllum, 1.5);
      shared.uSunColorTOA.value.set(m * 0.95, m, m * 1.08);
    }
    const cu = this.pipeline.compositeUniforms;
    cu.uLST.value = c.lst;
    cu.uMoonIllum.value = c.moonIllum;
    cu.uMoonLitDir.value.copy(c.sunDir).sub(c.moonDir.clone().multiplyScalar(0.0026)).normalize();
    cu.uStarVis.value = THREE.MathUtils.smoothstep(-c.sunAlt, 4, 16);
  }

  frame() {
    const now = performance.now();
    let dt = (now - this.last) / 1000;
    this.last = now;
    dt = Math.min(dt, 0.1);
    this.elapsed += dt;
    this.frameMs = this.frameMs * 0.9 + dt * 1000 * 0.1;
    this.fps = 1000 / this.frameMs;
    if (!this.ready) return;
    const cpu0 = performance.now();
    this.frameCount++;
    const r = this.renderer;
    r.info.reset();

    // time of day
    if (this.time.rate !== 0) {
      this.time.hour += dt * this.time.rate;
      while (this.time.hour >= 24) { this.time.hour -= 24; this.advanceDay(1); }
      while (this.time.hour < 0) { this.time.hour += 24; this.advanceDay(-1); }
    }
    this.updateCelestial();

    // player
    this.controller.update(dt, this.input);
    this.hike?.update(dt, this.input);
    for (const s of this.systems) s.update(dt, this);
    this.controller.applyToCamera(this.camera);
    this.input.endFrame();

    const cam = this.camera;
    shared.uCamPos.value.copy(cam.position);
    shared.uCurvOrigin.value.copy(cam.position);
    shared.uTime.value = this.elapsed;
    this.pipeline.compositeUniforms.uCurvOrigin.value.copy(cam.position);
    this.pipeline.compositeUniforms.uValley.value.w = this.elapsed;

    // weather -> sky, clouds, fog, surfaces
    this.applyWeather(dt);
    const wc = this.weather.current;
    this.gpu.poll();
    this.gpu.begin('sky');
    this.atmosphere.update(r, cam.position, this.celestial.sunDir, this.celestial.moonDir, this.celestial.moonIllum,
      this.clouds.active ? THREE.MathUtils.smoothstep(wc.cloudCoverage, 0.35, 0.98) : 0, wc.cloudDarkness);
    this.clouds.update(dt, this.weather.wind, cam, this.pipeline.depthTexture, this.weather.lightningFlash, this.weather.lightningPos);
    this.gpu.end();

    // geometry LOD
    this.terrain.update(cam);
    for (const s of this.instanceSystems) s.update(cam);
    this.vegetation?.update(cam);

    // shadows
    const t0 = r.info.render.triangles, c0 = r.info.render.calls;
    this.gpu.begin('shadows');
    this.shadows.update(r, cam, shared.uSunDir.value, true, Math.max(0, this.controller.heightAboveGround));
    this.gpu.end();
    this.passStats.shadowTris = r.info.render.triangles - t0;
    this.passStats.shadowCalls = r.info.render.calls - c0;

    // main pass, composite, post
    const t1 = r.info.render.triangles, c1 = r.info.render.calls;
    this.gpu.begin('scene');
    this.pipeline.renderScene(this.scene, cam);
    this.gpu.end();
    this.passStats.mainTris = r.info.render.triangles - t1;
    this.passStats.mainCalls = r.info.render.calls - c1;
    this.beforeComposite?.(dt);
    this.gpu.begin('clouds');
    this.clouds.render();
    this.gpu.end();
    this.pipeline.setClouds(this.clouds.rt.texture, this.clouds.active);
    this.gpu.begin('composite');
    this.pipeline.renderComposite(cam);
    this.gpu.end();
    this.precip.update(this.elapsed, cam, wc.rain, wc.snow, this.weather.wind, this.pipeline.depthTexture,
      this.atmosphere.skyIrr.texture, this.pipeline.compositeUniforms.uResolution.value, this.weather.lightningFlash);
    this.lightning.update(dt, this.weather.lightningFlash);
    if (this.lightning.active) {
      r.setRenderTarget(this.pipeline.compositeRT);
      const ac = r.autoClear;
      r.autoClear = false;
      r.render(this.lightning.scene, cam);
      r.autoClear = ac;
      r.setRenderTarget(null);
    }
    if (this.precip.active) {
      r.setRenderTarget(this.pipeline.compositeRT);
      const ac = r.autoClear;
      r.autoClear = false;
      r.render(this.precip.scene, cam);
      r.autoClear = ac;
      r.setRenderTarget(null);
    }
    this.afterComposite?.(dt);
    this.gpu.begin('post');
    this.pipeline.renderPost(dt, this.elapsed);
    this.gpu.end();
    this.audio.update(dt, {
      wind: wc.windSpeed, rain: wc.rain, snow: wc.snow,
      flySpeed: this.controller.mode === 'fly' ? this.controller.velocity.length() : 0,
      altitudeAGL: Math.max(0, this.controller.heightAboveGround), sunAlt: this.celestial.sunAlt, precipQuiet: false,
    });
    this.onFrame?.(dt);
    this.cpuMs = this.cpuMs * 0.9 + (performance.now() - cpu0) * 0.1;
  }

  cpuMs = 0;

  private buildTrailGrid() {
    const add = (x0: number, z0: number, x1: number, z1: number, paved: boolean) => {
      const cx = Math.floor((x0 + x1) / 2 / 10), cz = Math.floor((z0 + z1) / 2 / 10);
      for (let i = -1; i <= 1; i++) for (let j = -1; j <= 1; j++) {
        const k = (cx + i + 10000) * 20000 + (cz + j + 10000);
        let a = this.trailGrid.get(k);
        if (!a) this.trailGrid.set(k, (a = []));
        a.push([x0, z0, x1, z1, paved]);
      }
    };
    for (const t of this.osm.trails) for (let i = 0; i < t.pts.length - 1; i++) add(t.pts[i][0], t.pts[i][1], t.pts[i + 1][0], t.pts[i + 1][1], t.paved);
    for (const r of this.osm.roads) for (let i = 0; i < r.pts.length - 1; i++) add(r.pts[i][0], r.pts[i][1], r.pts[i + 1][0], r.pts[i + 1][1], true);
  }

  surfaceAt(x: number, z: number): 'trail' | 'rock' | 'ground' | 'snow' | 'paved' {
    if (this.weather.snowCover > 0.35) return 'snow';
    const a = this.trailGrid.get((Math.floor(x / 10) + 10000) * 20000 + (Math.floor(z / 10) + 10000));
    if (a) {
      for (const [x0, z0, x1, z1, paved] of a) {
        const dx = x1 - x0, dz = z1 - z0;
        const t = Math.max(0, Math.min(1, ((x - x0) * dx + (z - z0) * dz) / (dx * dx + dz * dz + 1e-9)));
        if (Math.hypot(x - x0 - dx * t, z - z0 - dz * t) < 1.4) return paved ? 'paved' : 'trail';
      }
    }
    const n = this.terrainData.normalAt(x, z);
    return n.y < 0.75 ? 'rock' : 'ground';
  }

  readonly passStats = { shadowTris: 0, shadowCalls: 0, mainTris: 0, mainCalls: 0 };

  private applyWeather(dt: number) {
    const w = this.weather;
    w.update(dt, this.camera.position);
    const c = w.current;
    const cp = this.clouds.params;
    cp.coverage = c.cloudCoverage;
    cp.base = c.cloudBase;
    cp.thickness = c.cloudThickness;
    cp.density = c.cloudDensity;
    cp.type = c.cloudType;
    cp.darkness = c.cloudDarkness;
    cp.erosion = c.cloudErosion;
    cp.anvil = c.cloudAnvil;
    this.atmosphere.params.mieScale = c.haze;
    shared.uCloudFar.value = 1 - 0.9 * Math.pow(THREE.MathUtils.clamp(c.cloudCoverage, 0, 1), 1.6) * Math.min(1, c.cloudDensity);
    const cu = this.pipeline.compositeUniforms;
    cu.uFog.value.set(c.fogDensity, 2300, c.fogScale, c.valleyFog);
    cu.uValley.value.x = c.valleyTop;
    cu.uValley.value.y = 70;
    cu.uValley.value.z = 26;
    cu.uPrecipHaze.value = c.rain * 0.0025 + c.snow * 0.006;
    cu.uLightning.value = w.lightningFlash;
    cu.uLightningPos.value.copy(w.lightningPos);
    shared.uWetness.value = w.wetness;
    shared.uSnowCover.value = w.snowCover;
    shared.uLightning.value = w.lightningFlash * 0.6;
    shared.uWind.value.set(Math.cos(c.windDir), Math.sin(c.windDir), c.windSpeed, this.elapsed);
  }
  beforeComposite: ((dt: number) => void) | null = null;
  afterComposite: ((dt: number) => void) | null = null;

  private advanceDay(d: number) {
    const dt = new Date(Date.UTC(this.time.year, this.time.month, this.time.day + d));
    this.time.year = dt.getUTCFullYear();
    this.time.month = dt.getUTCMonth();
    this.time.day = dt.getUTCDate();
  }

  diagnostics() {
    const info = this.renderer.info;
    return {
      fps: Math.round(this.fps),
      frameMs: +this.frameMs.toFixed(2),
      cpuMs: +this.cpuMs.toFixed(2),
      gpuMs: this.gpu ? Object.fromEntries(Object.entries(this.gpu.ms).map(([k, v]) => [k, +v.toFixed(2)])) : null,
      calls: info.render.calls,
      triangles: info.render.triangles,
      passes: { ...this.passStats },
      shadowBreakdown: this.shadows ? { ...this.shadows.breakdown } : null,
      geometries: info.memory.geometries,
      textures: info.memory.textures,
      terrainNodes: this.terrain?.nodesDrawn,
      hoodoos: this.hoodoos?.system.stats(),
      props: this.props ? this.props.systems.reduce((a, s) => a + s.count, 0) : 0,
      trees: this.vegetation?.meshes.stats(),
      impostors: this.vegetation?.impostors?.count ?? 0,
      warnings: this.loadWarnings,
      position: this.controller?.position.toArray().map((v) => +v.toFixed(1)),
      mode: this.controller?.mode,
      sunAlt: this.celestial ? +this.celestial.sunAlt.toFixed(2) : null,
      sunAz: this.celestial ? +this.celestial.sunAz.toFixed(2) : null,
      time: { ...this.time },
    };
  }
}
